/**
 * Camada de raios no mapa: cada flash do GLM no quadro de 10 min exibido,
 * uma estrela amarela pequena, trocando a cada quadro do loop.
 *
 * - Um quadro de tarde no verão chega a dezenas de milhares de flashes: os
 *   pontos vão em WebGL (`WebGLPointsLayer`), e a exportação lê o canvas
 *   (o OpenLayers cria o contexto com `preserveDrawingBuffer`).
 * - Os quadros do loop são baixados assim que a camada liga (4 por vez, do
 *   quadro atual para trás), como a fumaça. O quadro mais novo ainda está
 *   recebendo arquivos (um a cada 20 s): ele é guardado com o número de
 *   arquivos lidos, e o índice novo com mais arquivos o baixa de novo.
 * - A reprodução espera os raios do próximo quadro (`registerFrameGate`) e a
 *   exportação, os do atual.
 */
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { isAbortError } from '../lib/fetchRetry';
import { subscribeSelector } from '../lib/store';
import { registerFrameGate } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { lonLatToMap } from '../map/projection';
import { Z } from '../map/zIndex';
import { shownTimeOf, useTimeStore } from '../state/time';
import { carregarQuadro, RAIOS_ATRIBUICAO, type QuadroRaios } from './api';
import { useRaiosStore, type RaiosNoQuadro } from './store';
import { quadroParaInstante } from './tempo';

/** Amarelo do raio: distinto do laranja dos focos e do bege da fumaça. */
export const COR_RAIO = '#fde047';

/** Estrela de 4 pontas (o clarão), com borda escura para aparecer sobre nuvem branca. */
const ESTILO = {
  'shape-points': 4,
  'shape-radius': 5,
  'shape-radius2': 1.6,
  'shape-fill-color': COR_RAIO,
  'shape-stroke-color': 'rgba(30, 20, 0, 0.75)',
  'shape-stroke-width': 1,
};

const PARALELOS = 4;

type Entrada =
  | { estado: 'carregando'; ctrl: AbortController; a: number }
  | { estado: 'ok'; feicoes: Feature<Point>[]; a: number }
  | { estado: 'falhou'; a: number };

/** [lon, lat, t]… → pontos na projeção do mapa. */
export function pontosDoQuadro(v: Float64Array): Feature<Point>[] {
  const out: Feature<Point>[] = [];
  for (let k = 0; k + 2 < v.length; k += 3) {
    const [x, y] = lonLatToMap(v[k]!, v[k + 1]!);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push(new Feature(new Point([x, y])));
  }
  return out;
}

export class RaiosLayerManager {
  readonly layer: WebGLPointsLayer<VectorSource<Feature<Point>>>;
  private readonly source: VectorSource<Feature<Point>>;
  private readonly cache = new Map<string, Entrada>();
  private fila: QuadroRaios[] = [];
  private emVoo = 0;
  private naTela: string | null = null;
  private tempos: number[] = [];
  private quadros: QuadroRaios[] = [];
  private esperas = new Map<string, (() => void)[]>();
  private readonly paradas: (() => void)[] = [];

  constructor(private readonly map: OlMap) {
    this.source = new VectorSource<Feature<Point>>({ attributions: RAIOS_ATRIBUICAO });
    this.layer = new WebGLPointsLayer({
      source: this.source,
      style: ESTILO,
      zIndex: Z.RAIOS,
      className: 'ol-layer amaview-raios',
      properties: { id: 'raios', title: 'Raios (GOES-19 GLM)' },
    });
    map.addLayer(this.layer);

    this.paradas.push(
      useRaiosStore.subscribe((a, b) => {
        if (a.visivel !== b.visivel || a.indice !== b.indice) this.replanejar();
      }),
      useTimeStore.subscribe((a, b) => {
        if (a.frames !== b.frames || (a.index !== b.index && !loopLoadAllowed())) this.replanejar();
        else if (shownTimeOf(a) !== shownTimeOf(b)) this.mostrar();
      }),
      subscribeSelector(useLoopStore, (s) => loopLoadAllowed(s), () => this.replanejar()),
      registerFrameGate((i) => this.pronto(useTimeStore.getState().frames[i] ?? null)),
      registerFrameReadiness(() => this.quandoProntoAtual()),
    );
    this.replanejar();
  }

  private quadroDe(t: number | null): QuadroRaios | null {
    return quadroParaInstante(this.quadros, this.tempos, t);
  }

  /** O quadro guardado é o desta versão do índice (mesmo número de arquivos lidos)? */
  private atual(q: QuadroRaios): Entrada | undefined {
    const e = this.cache.get(q.c);
    return e && e.a === q.a ? e : undefined;
  }

  pronto(t: number | null): boolean {
    if (!useRaiosStore.getState().visivel) return true;
    const q = this.quadroDe(t);
    if (!q || q.n === 0) return true;
    const e = this.cache.get(q.c);
    return e !== undefined && e.estado !== 'carregando';
  }

  private quandoProntoAtual(): Promise<void> | void {
    const t = shownTimeOf(useTimeStore.getState());
    if (this.pronto(t)) return;
    const q = this.quadroDe(t)!;
    return new Promise((resolve) => {
      const lista = this.esperas.get(q.c) ?? [];
      lista.push(resolve);
      this.esperas.set(q.c, lista);
    });
  }

  private replanejar(): void {
    const { visivel, indice } = useRaiosStore.getState();
    this.layer.setVisible(visivel);
    this.quadros = indice?.quadros ?? [];
    this.tempos = this.quadros.map((q) => q.t);
    if (!visivel) {
      this.fila = [];
      this.mostrar();
      return;
    }
    const { frames, index } = useTimeStore.getState();
    const precisa = new Map<string, QuadroRaios>();
    const todoLoop = loopLoadAllowed();
    const ordem = frames.map((_, k) => (index - k + frames.length) % frames.length);
    for (const i of ordem) {
      const q = this.quadroDe(frames[i]);
      if (!q || q.n === 0) continue;
      if (todoLoop || i === index || this.cache.get(q.c)?.estado === 'ok') precisa.set(q.c, q);
    }
    for (const [c, e] of this.cache) {
      const q = precisa.get(c);
      // Saiu do loop, ou o quadro cresceu (o mais novo recebe arquivos): sai do cache.
      if (q && e.a === q.a) continue;
      if (q && e.estado === 'ok') continue; // fica na tela até o novo chegar
      if (e.estado === 'carregando') e.ctrl.abort();
      this.cache.delete(c);
    }
    this.fila = [...precisa.values()].filter((q) => !this.atual(q));
    this.atualizarProgresso(precisa.size);
    this.bombear();
    this.mostrar();
  }

  private atualizarProgresso(total?: number): void {
    let baixados = 0;
    let falhas = 0;
    for (const e of this.cache.values()) {
      if (e.estado === 'ok') baixados++;
      else if (e.estado === 'falhou') falhas++;
    }
    const antes = useRaiosStore.getState().progresso;
    const t = total ?? antes.total;
    if (antes.baixados !== baixados || antes.falhas !== falhas || antes.total !== t) {
      useRaiosStore.setState({ progresso: { baixados: Math.min(baixados, t), total: t, falhas } });
    }
  }

  private bombear(): void {
    while (this.emVoo < PARALELOS && this.fila.length) {
      const q = this.fila.shift()!;
      if (this.atual(q)) continue;
      const anterior = this.cache.get(q.c);
      const ctrl = new AbortController();
      // Um quadro que cresceu continua na tela (o anterior) até o novo chegar.
      if (anterior?.estado !== 'ok') this.cache.set(q.c, { estado: 'carregando', ctrl, a: q.a });
      this.emVoo++;
      carregarQuadro(q, ctrl.signal)
        .then((v) => {
          const e = this.cache.get(q.c);
          if (e && e.estado === 'carregando' && e.ctrl !== ctrl) return;
          this.cache.set(q.c, { estado: 'ok', feicoes: pontosDoQuadro(v), a: q.a });
          if (this.naTela === q.c) this.naTela = null; // redesenhar com o novo
        })
        .catch((e: unknown) => {
          if (isAbortError(e)) return;
          console.warn(`[AmaView] Raios ${q.c} não carregou:`, e);
          if (this.cache.get(q.c)?.estado !== 'ok') this.cache.set(q.c, { estado: 'falhou', a: q.a });
        })
        .finally(() => {
          this.emVoo--;
          this.atualizarProgresso();
          this.mostrar();
          const esperas = this.esperas.get(q.c);
          if (esperas && this.cache.get(q.c)?.estado !== 'carregando') {
            this.esperas.delete(q.c);
            esperas.forEach((r) => r());
          }
          this.bombear();
        });
    }
  }

  tentarDeNovo(): void {
    let algum = false;
    for (const [c, e] of this.cache) {
      if (e.estado === 'falhou') {
        this.cache.delete(c);
        algum = true;
      }
    }
    if (algum) this.replanejar();
  }

  private mostrar(): void {
    const { visivel } = useRaiosStore.getState();
    const t = shownTimeOf(useTimeStore.getState());
    const q = visivel ? this.quadroDe(t) : null;
    let situacao: RaiosNoQuadro;
    if (!q) {
      this.trocar(null, []);
      situacao = { estado: 'sem', t: null, n: 0 };
    } else if (q.n === 0) {
      this.trocar(q.c, []);
      situacao = { estado: 'ok', t: q.t, n: 0 };
    } else {
      const e = this.cache.get(q.c);
      if (e?.estado === 'ok') {
        this.trocar(q.c, e.feicoes);
        situacao = { estado: 'ok', t: q.t, n: e.feicoes.length };
      } else if (e?.estado === 'falhou') {
        this.trocar(null, []);
        situacao = { estado: 'falhou', t: q.t, n: 0 };
      } else {
        situacao = { estado: 'carregando', t: q.t, n: 0 };
        if (!e) {
          this.fila.unshift(q);
          this.bombear();
        }
      }
    }
    const antes = useRaiosStore.getState().noQuadro;
    if (antes.estado !== situacao.estado || antes.t !== situacao.t || antes.n !== situacao.n) useRaiosStore.setState({ noQuadro: situacao });
  }

  private trocar(c: string | null, feicoes: Feature<Point>[]): void {
    if (c === this.naTela) return;
    this.naTela = c;
    this.source.clear(true);
    if (feicoes.length) this.source.addFeatures(feicoes);
  }

  dispose(): void {
    this.paradas.forEach((p) => p());
    for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
    this.cache.clear();
    this.esperas.forEach((l) => l.forEach((r) => r()));
    this.esperas.clear();
    this.source.clear(true);
    this.map.removeLayer(this.layer);
    this.layer.dispose();
  }
}
